import React, { createContext, useContext, useState, useEffect } from 'react';
import { graphqlService } from '../services/graphqlService';

const UserContext = createContext();

export const useUsers = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUsers must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load users and roles from GraphQL on mount
    const loadData = async () => {
      setLoading(true);
      try {
        console.log('UserContext - Loading users and roles from GraphQL...');
        
        // Load users
        const users = await graphqlService.getAllUsers();
        console.log('UserContext - Received users:', users?.length || 0, 'users');
        if (users && users.length > 0) {
          console.log('UserContext - First user sample:', {
            _id: users[0]._id,
            name: users[0].name,
            email: users[0].email,
            hasName: !!users[0].name
          });
        }
        setUsers(Array.isArray(users) ? users : []);
        
        // Load roles
        const activeRoles = await graphqlService.getActiveRoles();
        console.log('UserContext - Received roles:', activeRoles?.length || 0, 'roles');
        setRoles(Array.isArray(activeRoles) ? activeRoles : []);
      } catch (error) {
        console.error('Error loading data from GraphQL:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        // Fallback to empty arrays if GraphQL fails
        setUsers([]);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const createUser = async (userData) => {
    setLoading(true);
    try {
      const input = {
        email: userData.email,
        name: userData.name,
        password: userData.password || 'default123',
        validEmails: userData.validEmails || [userData.email],
        validPasswords: userData.validPasswords || [userData.password || 'default123'],
        role: userData.role,
        department: userData.department,
        designation: userData.designation || '',
        specialization: userData.specialization || '',
        isActive: userData.isActive !== undefined ? userData.isActive : true
      };
      
      const newUser = await graphqlService.createUser(input);
      setUsers([...users, newUser]);
      setLoading(false);
      return newUser;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const updateUser = async (userId, userData) => {
    setLoading(true);
    try {
      const input = {
        ...userData,
        email: userData.email?.toLowerCase()
      };
      
      const updatedUser = await graphqlService.updateUser(userId, input);
      setUsers(users.map(u => u._id === userId ? updatedUser : u));
      setLoading(false);
      return updatedUser;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const deleteUser = async (userId) => {
    setLoading(true);
    try {
      await graphqlService.deleteUser(userId);
      setUsers(users.filter(user => user._id !== userId));
      setLoading(false);
      return true;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const getUser = async (userId) => {
    try {
      return await graphqlService.getUser(userId);
    } catch (error) {
      console.error('Error fetching user:', error);
      return users.find(user => user._id === userId || user.id === userId);
    }
  };

  const getUserByEmail = async (email) => {
    try {
      return await graphqlService.getUserByEmail(email);
    } catch (error) {
      console.error('Error fetching user by email:', error);
      return users.find(user => user.email === email);
    }
  };

  const getUsersByRole = async (role) => {
    try {
      return await graphqlService.getUsersByRole(role);
    } catch (error) {
      console.error('Error fetching users by role:', error);
      return users.filter(user => user.role === role && user.isActive);
    }
  };

  const getUsersByDepartment = async (department) => {
    try {
      return await graphqlService.getUsersByDepartment(department);
    } catch (error) {
      console.error('Error fetching users by department:', error);
      return users.filter(user => user.department === department && user.isActive);
    }
  };

  const getAllRoles = () => {
    // Return active roles from database, excluding admin from assignments
    return roles
      .filter(role => role.isActive && role.name !== 'admin')
      .map(role => role.name)
      .sort((a, b) => {
        const roleA = roles.find(r => r.name === a);
        const roleB = roles.find(r => r.name === b);
        return (roleA?.order || 0) - (roleB?.order || 0);
      });
  };
  
  const getAllRolesForUserManagement = () => {
    // Return all active roles including admin for user management
    return roles
      .filter(role => role.isActive)
      .map(role => ({
        value: role.name,
        label: role.displayName || role.name
      }))
      .sort((a, b) => {
        const roleA = roles.find(r => r.name === a.value);
        const roleB = roles.find(r => r.name === b.value);
        return (roleA?.order || 0) - (roleB?.order || 0);
      });
  };

  const getAllDepartments = () => {
    const departments = [...new Set(users.map(user => user.department))];
    return departments;
  };

  const getAllDesignations = () => {
    const designations = [...new Set(users.map(user => user.designation))];
    return designations.filter(d => d); // Filter out null/undefined values
  };

  const getAllSpecializations = () => {
    const specializations = [...new Set(users.map(user => user.specialization))];
    return specializations.filter(s => s); // Filter out null/undefined values
  };

  const searchUsers = async (query) => {
    try {
      return await graphqlService.searchUsers(query);
    } catch (error) {
      console.error('Error searching users:', error);
      const lowercaseQuery = query.toLowerCase();
      return users.filter(user => 
        user.name.toLowerCase().includes(lowercaseQuery) ||
        user.email.toLowerCase().includes(lowercaseQuery) ||
        user.department.toLowerCase().includes(lowercaseQuery) ||
        (user.designation && user.designation.toLowerCase().includes(lowercaseQuery)) ||
        (user.specialization && user.specialization.toLowerCase().includes(lowercaseQuery))
      );
    }
  };

  const getUserStats = async () => {
    try {
      return await graphqlService.getUserStats();
    } catch (error) {
      console.error('Error fetching user stats:', error);
      // Fallback to local calculation
      const totalUsers = users.length;
      const activeUsers = users.filter(user => user.isActive).length;
      const usersByRole = users.reduce((acc, user) => {
        acc[user.role] = (acc[user.role] || 0) + 1;
        return acc;
      }, {});
      
      const usersByDepartment = users.reduce((acc, user) => {
        acc[user.department] = (acc[user.department] || 0) + 1;
        return acc;
      }, {});

      const usersByDesignation = users.reduce((acc, user) => {
        if (user.designation) {
          acc[user.designation] = (acc[user.designation] || 0) + 1;
        }
        return acc;
      }, {});

      const usersBySpecialization = users.reduce((acc, user) => {
        if (user.specialization) {
          acc[user.specialization] = (acc[user.specialization] || 0) + 1;
        }
        return acc;
      }, {});

      return {
        totalUsers,
        activeUsers,
        usersByRole,
        usersByDepartment,
        usersByDesignation,
        usersBySpecialization
      };
    }
  };

  const value = {
    users,
    loading,
    createUser,
    updateUser,
    deleteUser,
    getUser,
    getUserByEmail,
    getUsersByRole,
    getUsersByDepartment,
    getAllRoles,
    getAllRolesForUserManagement,
    getAllDepartments,
    getAllDesignations,
    getAllSpecializations,
    searchUsers,
    getUserStats
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
